import React from 'react';
import { Input } from '../../../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../ui/select';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { getAllProductTypes, INSURANCE_PRODUCT_ICONS } from '../../../../lib/insurance-product-icons';

interface FiltersBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  productTypeFilter: string;
  onProductTypeChange: (type: string) => void;
  outcomeFilter: string;
  onOutcomeChange: (outcome: string) => void;
  dateRangeFilter: string;
  onDateRangeChange: (range: string) => void;
  filteredCount: number;
  onStartNew?: () => void;
}

const FiltersBar: React.FC<FiltersBarProps> = ({
  searchQuery,
  onSearchChange,
  productTypeFilter,
  onProductTypeChange,
  outcomeFilter,
  onOutcomeChange,
  dateRangeFilter,
  onDateRangeChange,
  filteredCount,
  onStartNew,
}) => {
  const productTypes = getAllProductTypes();

  return (
    <div className="bg-card border-b border-border px-6 py-4 pr-24">
      <div className="flex items-center justify-between gap-3">
        {/* Left: Search + Filters */}
        <div className="flex items-center gap-3 flex-1 max-w-2xl">
          {/* Search Bar */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by case title or customer name..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>

          {/* Filter: Product Type */}
          <Select value={productTypeFilter} onValueChange={onProductTypeChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Product Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Products</SelectItem>
              {productTypes.map(({ value, label }) => {
                const config = INSURANCE_PRODUCT_ICONS[value];
                const Icon = config.icon;
                return (
                  <SelectItem key={value} value={value}>
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${config.color}`} />
                      <span>{label}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Filter: Outcome */}
          <Select value={outcomeFilter} onValueChange={onOutcomeChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Outcome" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Outcomes</SelectItem>
              <SelectItem value="upheld">Likely Upheld</SelectItem>
              <SelectItem value="not_upheld">Likely Not Upheld</SelectItem>
            </SelectContent>
          </Select>

          {/* Filter: Date Range */}
          <Select value={dateRangeFilter} onValueChange={onDateRangeChange}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Right: Count and Action */}
        <div className="flex items-center gap-3">
          <Badge variant="outline">
            {filteredCount} {filteredCount === 1 ? 'case' : 'cases'}
          </Badge>
          {onStartNew && (
            <Button size="default" className="gap-2" onClick={onStartNew}>
              <PlusIcon className="w-5 h-5" />
              Start New Case
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FiltersBar;
